<template>
    <div class="flex justify-end">
        <span class="inline-block">
            <Tippy>
                <IconHelpCircle
                    class="h-6 w-6 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                />
                <template #content>
                    <span class="whitespace-pre-line">{{ $t('messages.sociogram_arrows_explanation') }}</span>
                </template>
            </Tippy>
        </span>
    </div>
    <div>
        <div>
            <SelectAllNoneButtons
                :max-count="filteredStudents.length"
                :selected-count="filteredStudents.filter((s) => s.is_selected).length"
                @select-all="selectAllStudents()"
                @select-none="deselectAllStudents()"
                class="flex"
            />
        </div>
        <div>{{ $t('messages.select') }}:</div>
        <div class="flex -space-x-1 flex-wrap relative z-0">
            <label v-for="student in filteredStudents" :key="student.id">
                <input type="checkbox" v-model="student.is_selected" class="hidden" />

                <img
                    :title="student.name"
                    :src="student.image_path"
                    class="relative z-30 inline-block h-8 w-8 rounded-lg ring-2 ring-white"
                    :class="[
                        student.is_negative ? 'bg-red-500' : 'bg-indigo-500',
                        { 'opacity-50': !student.is_selected },
                    ]"
                    alt=""
                />
            </label>
        </div>
    </div>
    <div class="mt-3">
        <NetworkGraph :edges="info.edges" :nodes="info.nodes" :options="options" />
    </div>
    <div>
        <details open>
            <summary class="font-semibold">{{ $tc('messages.students', 2) }}</summary>
            <div class="flex gap-2">
                <div>
                    <div class="text-sm">
                        {{ $t('messages.sort_order') }}
                    </div>
                    <div class="flex gap-x-2 items-center">
                        <label>
                            <IconSortAZ
                                :class="sortBy === 'full_name' ? 'text-indigo-400' : 'text-gray-400'"
                                class="h-10 w-10 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                            />
                            <input
                                type="radio"
                                name="sort_by"
                                class="sr-only"
                                id="full_name"
                                value="full_name"
                                v-model="sortBy"
                            />
                        </label>
                        <label>
                            <IconArrowBarRight
                                :class="sortBy === 'from_length' ? 'text-indigo-400' : 'text-gray-400'"
                                class="h-10 w-10 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                            />
                            <input
                                type="radio"
                                name="sort_by"
                                class="sr-only"
                                id="from_length"
                                value="from_length"
                                v-model="sortBy"
                            />
                        </label>

                        <label>
                            <IconArrowBarToLeft
                                :class="sortBy === 'to_length' ? 'text-indigo-400' : 'text-gray-400'"
                                class="h-10 w-10 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                            />
                            <input
                                type="radio"
                                name="sort_by"
                                class="sr-only"
                                id="to_length"
                                value="to_length"
                                v-model="sortBy"
                            />
                        </label>
                    </div>
                </div>
                <div>
                    <div class="text-sm">
                        {{ $t('messages.bidirectional') }}
                    </div>
                    <div class="flex gap-x-2 items-center">
                        <label>
                            <IconRefresh
                                :class="bidirectional === true ? 'text-indigo-400' : 'text-gray-400'"
                                class="h-10 w-10 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                            />
                            <input type="checkbox" name="bidirectional" class="sr-only" v-model="bidirectional" />
                        </label>
                    </div>
                </div>
            </div>
            <AppWell>
                <div class="mt-3">
                    <div class="md:grid md:grid-cols-6 md:gap-6" v-auto-animate>
                        <div v-for="student in sortedStudents" :key="student.id">
                            <AppCard>
                                <div class="">
                                    <div class="flex items-center gap-x-2">
                                        <img
                                            :title="student.name"
                                            :src="student.image_path"
                                            class="relative z-30 inline-block h-8 w-8 rounded-lg ring-2 ring-white"
                                            alt=""
                                        />
                                        <div>
                                            <button @click="selectStudent(student)">
                                                <span>
                                                    {{ student.name }}
                                                </span>
                                            </button>
                                            <div>
                                                <div
                                                    class="inline-block"
                                                    :title="$t('messages.bidirectional')"
                                                    v-if="bidirectionalStudentIds.has(student.id)"
                                                >
                                                    <IconRefresh
                                                        class="h-4 w-4 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                                                    />
                                                </div>
                                                &nbsp;
                                            </div>
                                        </div>
                                    </div>

                                    <div class="grid grid-cols-2 gap-6 mt-1">
                                        <Tippy interactive placement="bottom-end">
                                            <div class="flex gap-x-2 items-center">
                                                <IconArrowBarRight
                                                    class="h-6 w-6 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                                                />
                                                <span>{{ student.from.length }}</span>
                                            </div>
                                            <template #content>
                                                <IconCircleOff
                                                    v-if="student.from.length === 0"
                                                    class="h-10 w-10 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                                                />
                                                <div class="p-3" v-else>
                                                    <StudentsProfileStackFiltered
                                                        :students="students"
                                                        :selected="student.from"
                                                        class="inline-block h-8"
                                                        image-property="image_path"
                                                    />
                                                </div>
                                            </template>
                                        </Tippy>
                                        <Tippy interactive placement="bottom-end">
                                            <div class="flex gap-x-2 items-center">
                                                <IconArrowBarToLeft
                                                    class="h-6 w-6 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                                                />
                                                <span>{{ student.to.length }}</span>
                                            </div>
                                            <template #content>
                                                <IconCircleOff
                                                    v-if="student.from.to === 0"
                                                    class="h-10 w-10 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                                                />
                                                <div class="p-3" v-else>
                                                    <StudentsProfileStackFiltered
                                                        :students="students"
                                                        :selected="student.to"
                                                        class="inline-block h-8"
                                                        image-property="image_path"
                                                    />
                                                </div>
                                            </template>
                                        </Tippy>
                                    </div>
                                </div>
                            </AppCard>
                        </div>
                    </div>
                </div>
            </AppWell>
        </details>
    </div>
</template>

<script>
export default {
    name: 'NetworkGraphSociogramStudents',
};
</script>
<script setup>
import { computed, ref } from 'vue';
import NetworkGraph from '@/components/pageSpecific/NetworkGraph.vue';
import {
    IconHelpCircle,
    IconArrowBarRight,
    IconArrowBarToLeft,
    IconCircleOff,
    IconSortAZ,
    IconRefresh,
} from '@tabler/icons-vue';
import { Tippy } from 'vue-tippy';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import StudentsProfileStackFiltered from '@/components/StudentsProfileStackFiltered.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';

const props = defineProps({
    edges: {
        required: true,
        type: Array,
    },
    students: {
        required: true,
        type: Array,
    },
    isNegative: {
        type: Boolean,
        default: false,
    },
});

const bidirectionalSet = new Set(props.edges.map((e) => e.from + '-' + e.to));
const bidirectional = ref(false);

const bidirectionalStudentIds = new Set();

props.edges.forEach((edge) => {
    if (bidirectionalSet.has(edge.to + '-' + edge.from)) {
        bidirectionalStudentIds.add(edge.from);
        bidirectionalStudentIds.add(edge.to);
    }
});

const filteredStudents = ref(
    props.students.map((s) => {
        return {
            ...s,
            is_selected: true,
        };
    }),
);

const selectFromStudentIds = (ids) => {
    filteredStudents.value.forEach((s) => {
        s.is_selected = ids.includes(s.id);
    });
};

const selectStudent = (student) => {
    let ids = [];
    if (bidirectional.value) {
        ids = student.from
            .concat(student.to)
            .filter((id) => student.from.includes(id))
            .filter((id) => student.to.includes(id))
            .concat([student.id]);
    } else {
        ids = student.from.concat(student.to).concat([student.id]);
    }
    selectFromStudentIds(ids);
};

const selectAllStudents = () => {
    const ids = filteredStudents.value.map((s) => s.id);
    selectFromStudentIds(ids);
};

const deselectAllStudents = () => {
    selectFromStudentIds([]);
};

const sortBy = ref('full_name');

const sortedStudents = computed(() => {
    return Array.from(filteredStudents.value).sort((a, b) => {
        if (sortBy.value === 'full_name') {
            return a.full_name.localeCompare(b.full_name);
        }
        return a[sortBy.value] - b[sortBy.value];
    });
});

const info = computed(() => {
    const nodes = [];
    const edges = [];
    filteredStudents.value
        .filter((t) => t.is_selected)
        .forEach((student) => {
            nodes.push({
                id: `s_${student.id}`,
                shape: 'circularImage',
                image: student.image_path,
                group: 'student',
                title: student.name,
                label: student.name,
            });
        });

    props.edges.forEach((edge) => {
        let color = props.isNegative ? '#ef4444' : '#6366f1';

        if (bidirectional.value === false || (bidirectional.value && bidirectionalSet.has(edge.to + '-' + edge.from))) {
            edges.push({
                from: `s_${edge.from}`,
                to: `s_${edge.to}`,
                color: color,
            });
        }
    });

    return {
        nodes,
        edges,
    };
});

const options = {
    nodes: {
        borderWidth: 4,
        size: 30,
        color: {
            border: '#222222',
            background: '#666666',
        },
    },
    edges: {
        color: {
            color: 'green',
            highlight: '#2ed300',
            hover: '#848484',
            inherit: 'from',
            opacity: 0.4,
        },
        arrows: {
            to: {
                enabled: true,
                scaleFactor: 0.5,
                type: 'arrow',
            },
        },
        scaling: {
            customScalingFunction: function (min, max, total, value) {
                return value / total;
            },
            min: 1,
            max: 25,
        },
        selectionWidth: function (width) {
            return width * 2;
        },
    },
};
</script>
